import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createExportAssetExpense, getExportAssetExpenses, getExportAssetExpensesForExport, updateExportAssetExpense, deleteExportAssetExpense, getBalance, updateBalance } from "./service";
import { toast } from "react-toastify";

const initialState = {
    create: {
        date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
        name: '', // Kept for backward compatibility
        category: '',
        description: '',
        amount: '',
    },
    errors: {
        date: '',
        name: '', // Kept for backward compatibility
        category: '',
        description: '',
        amount: '',
    },
    isCreateModalOpen: false,
    isEditModalOpen: false,
    selectedExpense: null,
    isLoading: false,
    isError: false,
    expenses: [],
    filter: {
        page: 1,
        limit: 10,
        dateFrom: '',
        dateTo: '',
        category: '', // Category filter (ID)
        search: '', // Search by description only
    },
    pagination: {
        currentPage: 1,
        totalPages: 0,
        totalCount: 0,
        pageSize: 10,
    },
    balance: 0,
    isBalanceLoading: false,
};

export const createExpense = createAsyncThunk(
    "exportAssetExpense/createExpense",
    async (expenseData, { rejectWithValue, dispatch, getState }) => {
        try {
            const response = await createExportAssetExpense(expenseData);
            // Refresh the list after create
            const filter = getState().exportAssetExpense.filter;
            dispatch(getExpensesThunk(filter));
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message || 'An unexpected error occurred while creating the expense.');
        }
    }
);

export const getExpensesThunk = createAsyncThunk(
    "exportAssetExpense/getExpensesThunk",
    async (query, { rejectWithValue }) => {
        try {
            const response = await getExportAssetExpenses(query);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message || 'An unexpected error occurred while fetching expenses.');
        }
    }
);

export const updateExpense = createAsyncThunk(
    "exportAssetExpense/updateExpense",
    async ({ id, expenseData }, { rejectWithValue, dispatch, getState }) => {
        try {
            const response = await updateExportAssetExpense(id, expenseData);
            // Refresh the list after update
            const filter = getState().exportAssetExpense.filter;
            dispatch(getExpensesThunk(filter));
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message || 'An unexpected error occurred while updating the expense.');
        }
    }
);

export const deleteExpense = createAsyncThunk(
    "exportAssetExpense/deleteExpense",
    async (id, { rejectWithValue, dispatch, getState }) => {
        try {
            await deleteExportAssetExpense(id);
            // Refresh the list after delete
            const filter = getState().exportAssetExpense.filter;
            dispatch(getExpensesThunk(filter));
            return id;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message || 'An unexpected error occurred while deleting the expense.');
        }
    }
);

export const getExpensesForExportThunk = createAsyncThunk(
    "exportAssetExpense/getExpensesForExportThunk",
    async (query, { rejectWithValue }) => {
        try {
            const response = await getExportAssetExpensesForExport(query);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message || 'An unexpected error occurred while fetching expenses for export.');
        }
    }
);

export const fetchBalance = createAsyncThunk(
    "exportAssetExpense/fetchBalance",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getBalance();
            return response.data.balance;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message || 'An unexpected error occurred while fetching balance.');
        }
    }
);

export const updateBalanceThunk = createAsyncThunk(
    "exportAssetExpense/updateBalanceThunk",
    async (balance, { rejectWithValue, dispatch }) => {
        try {
            const response = await updateBalance(balance);
            dispatch(fetchBalance()); // Refresh balance after update
            return response.data.balance;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message || 'An unexpected error occurred while updating balance.');
        }
    }
);

const exportAssetExpenseSlice = createSlice({
    name: 'exportAssetExpense',
    initialState,
    reducers: {
        updateCreateField: (state, action) => {
            const { field, value } = action.payload;
            state.create[field] = value;
            // Clear error for this field when user starts typing
            if (state.errors[field]) {
                state.errors[field] = '';
            }
        },
        setError: (state, action) => {
            const { field, error } = action.payload;
            state.errors[field] = error;
        },
        clearErrors: (state) => {
            state.errors = initialState.errors;
        },
        openCreateModal: (state) => {
            state.isCreateModalOpen = true;
        },
        closeCreateModal: (state) => {
            state.isCreateModalOpen = false;
            state.create = initialState.create;
            state.errors = initialState.errors;
        },
        openEditModal: (state, action) => {
            state.isEditModalOpen = true;
            state.selectedExpense = action.payload;
            // Populate create form with expense data
            let dateValue = '';
            if (action.payload && action.payload.date) {
                try {
                    let date;
                    const payloadDate = action.payload.date;
                    
                    // Handle different date formats
                    if (typeof payloadDate === 'string') {
                        // If it's already in YYYY-MM-DD format, use it directly
                        if (payloadDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                            dateValue = payloadDate;
                        } else {
                            // Parse ISO string or other date string formats
                            date = new Date(payloadDate);
                            // Check if date is valid
                            if (!isNaN(date.getTime())) {
                                // Format as YYYY-MM-DD
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                dateValue = `${year}-${month}-${day}`;
                            } else {
                                console.warn('Invalid date string:', payloadDate);
                            }
                        }
                    } else if (payloadDate instanceof Date) {
                        // It's already a Date object
                        date = payloadDate;
                        if (!isNaN(date.getTime())) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            dateValue = `${year}-${month}-${day}`;
                        }
                    } else {
                        // Try to convert to Date (handles timestamps, etc.)
                        date = new Date(payloadDate);
                        if (!isNaN(date.getTime())) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            dateValue = `${year}-${month}-${day}`;
                        } else {
                            console.warn('Invalid date value:', payloadDate, 'Type:', typeof payloadDate);
                        }
                    }
                } catch (error) {
                    console.error('Error parsing date in openEditModal:', error, 'Date value:', action.payload.date, 'Type:', typeof action.payload.date);
                    dateValue = '';
                }
            } else {
                console.warn('No date found in action.payload:', action.payload);
            }
            
            state.create = {
                date: dateValue,
                name: action.payload.name || '', // Kept for backward compatibility
                category: action.payload.category?._id || action.payload.category || '',
                description: action.payload.description || '',
                amount: action.payload.amount || '',
            };
        },
        closeEditModal: (state) => {
            state.isEditModalOpen = false;
            state.selectedExpense = null;
            state.create = initialState.create;
            state.errors = initialState.errors;
        },
        updateFilter: (state, action) => {
            const { key, value } = action.payload;
            state.filter[key] = value;
            // Reset to page 1 when filter changes
            if (key !== 'page') {
                state.filter.page = 1;
            }
        },
        clearFilter: (state) => {
            state.filter = initialState.filter;
        },
    },
    extraReducers: (builder) => {
        builder
            // Create expense
            .addCase(createExpense.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
            })
            .addCase(createExpense.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isError = false;
                state.isCreateModalOpen = false;
                state.create = initialState.create;
                state.errors = initialState.errors;
                toast.success("Expense created successfully");
            })
            // Update expense
            .addCase(updateExpense.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
            })
            .addCase(updateExpense.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isError = false;
                state.isEditModalOpen = false;
                state.selectedExpense = null;
                state.create = initialState.create;
                state.errors = initialState.errors;
                toast.success("Expense updated successfully");
            })
            .addCase(updateExpense.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                toast.error(action.payload || "Failed to update expense");
            })
            // Delete expense
            .addCase(deleteExpense.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
            })
            .addCase(deleteExpense.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isError = false;
                toast.success("Expense deleted successfully");
            })
            .addCase(deleteExpense.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                toast.error(action.payload || "Failed to delete expense");
            })
            .addCase(createExpense.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                toast.error(action.payload || "Failed to create expense");
            })
            // Get expenses
            .addCase(getExpensesThunk.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
            })
            .addCase(getExpensesThunk.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isError = false;
                state.expenses = action.payload.expenses || [];
                state.pagination = action.payload.pagination || initialState.pagination;
            })
            .addCase(getExpensesThunk.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                toast.error(action.payload || "Failed to fetch expenses");
            })
            // Fetch balance
            .addCase(fetchBalance.pending, (state) => {
                state.isBalanceLoading = true;
            })
            .addCase(fetchBalance.fulfilled, (state, action) => {
                state.isBalanceLoading = false;
                state.balance = action.payload;
            })
            .addCase(fetchBalance.rejected, (state, action) => {
                state.isBalanceLoading = false;
                toast.error(action.payload || "Failed to fetch balance");
            })
            // Update balance
            .addCase(updateBalanceThunk.pending, (state) => {
                state.isBalanceLoading = true;
            })
            .addCase(updateBalanceThunk.fulfilled, (state, action) => {
                state.isBalanceLoading = false;
                state.balance = action.payload;
                toast.success("Balance updated successfully");
            })
            .addCase(updateBalanceThunk.rejected, (state, action) => {
                state.isBalanceLoading = false;
                toast.error(action.payload || "Failed to update balance");
            });
    },
});

export const {
    updateCreateField,
    setError,
    clearErrors,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    updateFilter,
    clearFilter,
} = exportAssetExpenseSlice.actions;

export default exportAssetExpenseSlice.reducer;

